import api from './api';

export interface ProfileData {
  first_name?: string;
  last_name?: string;
  phone?: string;
  company_name?: string;
  company_code?: string;
  vat_code?: string;
  company_address?: string;
  company_activity?: string;
  contact_person?: string;
  website_url?: string;
  location?: string;
  bio?: string;
  company_size?: '1-10' | '11-50' | '51-200' | '201-1000' | '1000+';
  linkedin_url?: string;
  is_recruitment_agency?: boolean;
}

export interface ProfileResponse {
  success: boolean;
  message?: string;
  data?: {
    user: any;
  };
}

class ProfileService {
  // Get current user's profile
  async getProfile(): Promise<ProfileResponse> {
    try {
      const response = await api.get('/profile');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch profile'
      };
    }
  }

  // Update profile
  async updateProfile(profileData: ProfileData): Promise<ProfileResponse> {
    try {
      const response = await api.put('/profile', profileData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to update profile'
      };
    }
  }

  // Upload profile image
  async uploadProfileImage(file: File): Promise<ProfileResponse> {
    try {
      const formData = new FormData();
      formData.append('profile_image', file);

      const response = await api.post('/profile/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to upload image'
      };
    }
  }

  // Delete profile image
  async deleteProfileImage(): Promise<ProfileResponse> {
    try {
      const response = await api.delete('/profile/image');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to delete image'
      };
    }
  }

  // Update profile with image
  async updateProfileWithImage(profileData: ProfileData, imageFile?: File): Promise<ProfileResponse> {
    try {
      const formData = new FormData();
      
      // Add all profile data to FormData
      Object.entries(profileData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });

      // Add image if provided
      if (imageFile) {
        formData.append('profile_image', imageFile);
      }

      const response = await api.put('/profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to update profile'
      };
    }
  }

  // Get profile image URL
  getProfileImageUrl(filename?: string): string | null {
    if (!filename) return null;
    return `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}/uploads/profiles/${filename}`;
  }
}

const profileService = new ProfileService();
export default profileService;
